import React, { useState, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { usersAPI, tenantAPI } from '../../services/api';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import UserAvatar from '../../components/UI/UserAvatar';
import {
  UserCircleIcon,
  SwatchIcon,
  ShieldCheckIcon,
  BellIcon,
  SunIcon,
  MoonIcon,
  CheckIcon,
  EyeIcon,
  EyeSlashIcon,
  BuildingOfficeIcon,
  PhotoIcon,
  TrashIcon,
  CameraIcon,
} from '@heroicons/react/24/outline';

const TABS = [
  { id: 'profile', label: 'Profile', icon: UserCircleIcon },
  { id: 'organization', label: 'Organization', icon: BuildingOfficeIcon, adminOnly: true },
  { id: 'appearance', label: 'Appearance', icon: SwatchIcon },
  { id: 'security', label: 'Security', icon: ShieldCheckIcon },
  { id: 'notifications', label: 'Notifications', icon: BellIcon },
];

const Settings = () => {
  const { user, updateUser } = useAuth();
  const { darkMode, toggleDarkMode, primaryColor, changeThemeColor, colorPresets } = useTheme();
  const [activeTab, setActiveTab] = useState('profile');
  const queryClient = useQueryClient();
  const logoInputRef = useRef(null);
  const avatarInputRef = useRef(null);

  const isAdmin = user?.role === 'admin';
  const visibleTabs = TABS.filter(t => !t.adminOnly || isAdmin);

  // Tenant data
  const { data: tenant, isLoading: tenantLoading } = useQuery({
    queryKey: ['tenant'],
    queryFn: async () => { const r = await tenantAPI.get(); return r.data.data; },
    enabled: isAdmin,
  });

  const logoUploadMutation = useMutation({
    mutationFn: (formData) => tenantAPI.uploadLogo(formData),
    onSuccess: () => {
      queryClient.invalidateQueries(['tenant']);
      toast.success('Logo uploaded successfully');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to upload logo'),
  });

  const logoDeleteMutation = useMutation({
    mutationFn: () => tenantAPI.deleteLogo(),
    onSuccess: () => {
      queryClient.invalidateQueries(['tenant']);
      toast.success('Logo removed');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to remove logo'),
  });

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be under 2MB');
      return;
    }
    const formData = new FormData();
    formData.append('logo', file);
    logoUploadMutation.mutate(formData);
  };

  // Avatar mutations
  const avatarUploadMutation = useMutation({
    mutationFn: (formData) => usersAPI.uploadAvatar(formData),
    onSuccess: (response) => {
      updateUser(response.data.data);
      toast.success('Avatar uploaded successfully');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to upload avatar'),
  });

  const avatarDeleteMutation = useMutation({
    mutationFn: () => usersAPI.deleteAvatar(),
    onSuccess: (response) => {
      updateUser(response.data.data);
      toast.success('Avatar removed');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to remove avatar'),
  });

  const handleAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Avatar must be under 2MB');
      return;
    }
    const formData = new FormData();
    formData.append('avatar', file);
    avatarUploadMutation.mutate(formData);
  };

  // Profile form
  const [profileForm, setProfileForm] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
  });

  // Password form
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // Notification prefs (local only)
  const [notifPrefs, setNotifPrefs] = useState({
    emailNotifications: true,
    leadAssignments: true,
    dealUpdates: true,
    quoteActivity: true,
    systemAlerts: true,
  });

  const profileMutation = useMutation({
    mutationFn: (data) => usersAPI.update(user._id, data),
    onSuccess: (response) => {
      updateUser(response.data.data);
      toast.success('Profile updated successfully');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to update profile'),
  });

  const passwordMutation = useMutation({
    mutationFn: (data) => usersAPI.changePassword(data),
    onSuccess: () => {
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success('Password updated successfully');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to update password'),
  });

  const handleProfileSubmit = (e) => {
    e.preventDefault();
    profileMutation.mutate(profileForm);
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    passwordMutation.mutate({ 
      currentPassword: passwordForm.currentPassword, 
      newPassword: passwordForm.newPassword 
    });
  };

  const COLOR_LABELS = {
    blue: { name: 'Blue', gradient: 'from-blue-500 to-blue-700' },
    purple: { name: 'Purple', gradient: 'from-purple-500 to-purple-700' },
    green: { name: 'Green', gradient: 'from-green-500 to-green-700' },
    orange: { name: 'Orange', gradient: 'from-orange-500 to-orange-700' },
    pink: { name: 'Pink', gradient: 'from-pink-500 to-pink-700' },
    teal: { name: 'Teal', gradient: 'from-teal-500 to-teal-700' },
  };

  const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.06 } } };
  const itemVariants = { hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0 } };

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-6">
      <motion.div variants={itemVariants}>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Settings</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Manage your account preferences</p>
      </motion.div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar Tabs */}
        <motion.div variants={itemVariants} className="lg:w-56 flex-shrink-0">
          <div className="card-premium rounded-2xl p-2">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-surface-700'
                }`}
              >
                <tab.icon className={`w-5 h-5 ${activeTab === tab.id ? 'text-primary-500' : ''}`} />
                {tab.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <motion.div variants={itemVariants} className="space-y-6">
              {/* User Info Card */}
              <div className="card-premium rounded-2xl p-6">
                <div className="flex items-center gap-4 mb-6 pb-6 border-b border-gray-100 dark:border-surface-700">
                  <div className="relative group">
                    <UserAvatar user={user} size="xl" />
                    <button
                      type="button"
                      onClick={() => avatarInputRef.current?.click()}
                      className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                      title="Change avatar"
                    >
                      <CameraIcon className="w-5 h-5 text-white" />
                    </button>
                    <input
                      ref={avatarInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleAvatarUpload}
                    />
                    {user?.avatar && (
                      <button
                        type="button"
                        onClick={() => avatarDeleteMutation.mutate()}
                        className="absolute -bottom-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                        title="Remove avatar"
                      >
                        <TrashIcon className="w-3 h-3 text-white" />
                      </button>
                    )}
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {user?.firstName} {user?.lastName}
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{user?.email}</p>
                    <span className={`mt-1 inline-block px-2 py-0.5 text-xs font-medium rounded-full ${
                      user?.role === 'admin' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                      : user?.role === 'manager' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                    }`}>{user?.role}</span>
                  </div>
                </div>

                <form onSubmit={handleProfileSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">First Name</label>
                      <input
                        type="text"
                        value={profileForm.firstName}
                        onChange={(e) => setProfileForm({ ...profileForm, firstName: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-surface-600 rounded-xl bg-white dark:bg-surface-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Last Name</label>
                      <input
                        type="text"
                        value={profileForm.lastName}
                        onChange={(e) => setProfileForm({ ...profileForm, lastName: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-surface-600 rounded-xl bg-white dark:bg-surface-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Email</label>
                    <input
                      type="email"
                      value={profileForm.email}
                      onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-surface-600 rounded-xl bg-white dark:bg-surface-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
                      required
                    />
                  </div>
                  <div className="flex justify-end pt-2">
                    <button type="submit" disabled={profileMutation.isPending} className="btn btn-primary">
                      {profileMutation.isPending ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          )}

          {/* Organization Tab (Admin only) */}
          {activeTab === 'organization' && isAdmin && (
            <motion.div variants={itemVariants} className="space-y-6">
              <div className="card-premium rounded-2xl p-6">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Organization Logo</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">Upload your company logo. It will appear on generated quotes and PDFs.</p>

                {tenantLoading ? (
                  <div className="h-32 bg-gray-100 dark:bg-surface-700 rounded-xl animate-pulse" />
                ) : (
                  <div className="flex items-start gap-6">
                    {/* Logo Preview */}
                    <div className="flex-shrink-0">
                      {tenant?.logo ? (
                        <div className="relative group">
                          <img
                            src={tenant.logo}
                            alt="Tenant logo"
                            className="w-32 h-32 object-contain rounded-xl border border-gray-200 dark:border-surface-600 bg-white dark:bg-surface-900 p-2"
                          />
                          <button
                            onClick={() => logoDeleteMutation.mutate()}
                            className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                          >
                            <TrashIcon className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <div className="w-32 h-32 rounded-xl border-2 border-dashed border-gray-300 dark:border-surface-600 flex flex-col items-center justify-center bg-gray-50 dark:bg-surface-900">
                          <PhotoIcon className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                          <p className="text-xs text-gray-400 mt-1">No logo</p>
                        </div>
                      )}
                    </div>

                    {/* Upload Controls */}
                    <div className="flex-1">
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/svg+xml"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                      <button
                        onClick={() => logoInputRef.current?.click()}
                        disabled={logoUploadMutation.isPending}
                        className="btn btn-primary text-sm"
                      >
                        <PhotoIcon className="w-4 h-4 mr-2" />
                        {logoUploadMutation.isPending ? 'Uploading...' : tenant?.logo ? 'Change Logo' : 'Upload Logo'}
                      </button>
                      <p className="text-xs text-gray-400 mt-2">Accepted formats: JPEG, PNG, SVG. Max 2MB.</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Organization Info */}
              <div className="card-premium rounded-2xl p-6">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Organization Details</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">These details appear on your quotes and documents</p>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between py-2 border-b border-gray-100 dark:border-surface-700">
                    <span className="text-gray-500 dark:text-gray-400">Name</span>
                    <span className="font-medium text-gray-900 dark:text-white">{tenant?.name || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100 dark:border-surface-700">
                    <span className="text-gray-500 dark:text-gray-400">Email</span>
                    <span className="font-medium text-gray-900 dark:text-white">{tenant?.email || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100 dark:border-surface-700">
                    <span className="text-gray-500 dark:text-gray-400">Phone</span>
                    <span className="font-medium text-gray-900 dark:text-white">{tenant?.phone || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-500 dark:text-gray-400">Plan</span>
                    <span className="font-medium text-gray-900 dark:text-white capitalize">{tenant?.subscription?.plan || 'Free'}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Appearance Tab */}
          {activeTab === 'appearance' && (
            <motion.div variants={itemVariants} className="space-y-6">
              {/* Dark Mode */}
              <div className="card-premium rounded-2xl p-6">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Dark Mode</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Toggle between light and dark theme</p>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => darkMode && toggleDarkMode()}
                    className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 transition-all ${
                      !darkMode ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-200 dark:border-surface-600 hover:border-gray-300'
                    }`}
                  >
                    <SunIcon className="w-5 h-5 text-amber-500" />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">Light</span>
                  </button>
                  <button
                    onClick={() => !darkMode && toggleDarkMode()}
                    className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 transition-all ${
                      darkMode ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-200 dark:border-surface-600 hover:border-gray-300'
                    }`}
                  >
                    <MoonIcon className="w-5 h-5 text-indigo-500" />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">Dark</span>
                  </button>
                </div>
              </div>

              {/* Theme Color */}
              <div className="card-premium rounded-2xl p-6">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Theme Color</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Choose your accent color</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {colorPresets.map((color) => {
                    const info = COLOR_LABELS[color] || { name: color, gradient: 'from-gray-500 to-gray-700' };
                    return (
                      <button
                        key={color}
                        onClick={() => changeThemeColor(color)}
                        className={`relative flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                          primaryColor === color
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-gray-200 dark:border-surface-600 hover:border-gray-300'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${info.gradient}`} />
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{info.name}</span>
                        {primaryColor === color && (
                          <CheckIcon className="w-4 h-4 text-primary-500 absolute top-2 right-2" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <motion.div variants={itemVariants} className="space-y-6">
              <div className="card-premium rounded-2xl p-6">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Change Password</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Update your account password</p>
                <form onSubmit={handlePasswordSubmit} className="space-y-4 max-w-md">
                  {[
                    { key: 'currentPassword', label: 'Current Password', show: showPasswords.current, toggle: 'current' },
                    { key: 'newPassword', label: 'New Password', show: showPasswords.new, toggle: 'new' },
                    { key: 'confirmPassword', label: 'Confirm New Password', show: showPasswords.confirm, toggle: 'confirm' },
                  ].map((field) => (
                    <div key={field.key}>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">{field.label}</label>
                      <div className="relative">
                        <input
                          type={field.show ? 'text' : 'password'}
                          value={passwordForm[field.key]}
                          onChange={(e) => setPasswordForm({ ...passwordForm, [field.key]: e.target.value })}
                          className="w-full px-3 py-2 pr-10 text-sm border border-gray-200 dark:border-surface-600 rounded-xl bg-white dark:bg-surface-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords({ ...showPasswords, [field.toggle]: !field.show })}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {field.show ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-end pt-2">
                    <button type="submit" disabled={passwordMutation.isPending} className="btn btn-primary">
                      {passwordMutation.isPending ? 'Updating...' : 'Update Password'}
                    </button>
                  </div>
                </form>
              </div>

              {/* Session Info */}
              <div className="card-premium rounded-2xl p-6">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Session</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Current session information</p>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between py-2 border-b border-gray-100 dark:border-surface-700">
                    <span className="text-gray-500 dark:text-gray-400">Role</span>
                    <span className="font-medium text-gray-900 dark:text-white capitalize">{user?.role}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100 dark:border-surface-700">
                    <span className="text-gray-500 dark:text-gray-400">Account Created</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-500 dark:text-gray-400">Organization</span>
                    <span className="font-medium text-gray-900 dark:text-white">{user?.tenantId?.name || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <motion.div variants={itemVariants}>
              <div className="card-premium rounded-2xl p-6">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Notification Preferences</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Choose what you want to be notified about</p>
                <div className="space-y-4">
                  {[
                    { key: 'emailNotifications', label: 'Email Notifications', desc: 'Receive email notifications for important events' },
                    { key: 'leadAssignments', label: 'Lead Assignments', desc: 'When a lead is assigned to you' },
                    { key: 'dealUpdates', label: 'Deal Updates', desc: 'When a deal you\'re involved in is updated' },
                    { key: 'quoteActivity', label: 'Quote Activity', desc: 'When quotes are viewed, accepted, or rejected' },
                    { key: 'systemAlerts', label: 'System Alerts', desc: 'Important system notifications' },
                  ].map((pref) => (
                    <div key={pref.key} className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-surface-700 last:border-0">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{pref.label}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{pref.desc}</p>
                      </div>
                      <button
                        onClick={() => setNotifPrefs({ ...notifPrefs, [pref.key]: !notifPrefs[pref.key] })}
                        className={`relative w-11 h-6 rounded-full transition-colors ${
                          notifPrefs[pref.key] ? 'bg-primary-500' : 'bg-gray-300 dark:bg-surface-600'
                        }`}
                      >
                        <motion.div
                          animate={{ x: notifPrefs[pref.key] ? 20 : 2 }}
                          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                          className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm"
                        />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default Settings;
